import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Clock,
  DollarSign,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Building2,
  Calendar,
  FileText
} from 'lucide-react';

interface RoleTailoredContent {
  role: string;
  role_display_name?: string;
  summary: string;
  key_actions?: string[];
  financial_implications?: string;
  time_impact?: string;
  deadline?: string;
}

interface RoleTailoredDocument {
  id: string;
  title: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  source?: string;
  source_link?: string;
  date?: string;
  role_tailored_content?: RoleTailoredContent[];
  simplified_analysis?: {
    one_sentence_summary?: string;
    paragraph_summary?: string;
    detailed_changes?: string[];
    key_requirements?: string[];
  };
  affected_departments?: string[];
  url?: string;
}

interface RoleTailoredDocumentCardProps {
  document: RoleTailoredDocument;
  selectedRole?: string;
  onClick?: () => void;
  viewMode?: 'list' | 'detail'; // 'list' hides role-specific content, 'detail' shows everything
}

const priorityConfig = {
  critical: {
    color: '#dc2626',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    label: 'Critical'
  },
  high: {
    color: '#ea580c',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
    label: 'High'
  },
  medium: {
    color: '#ca8a04',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200',
    label: 'Medium'
  },
  low: {
    color: '#6b7280',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
    label: 'Low'
  }
};

export const RoleTailoredDocumentCard: React.FC<RoleTailoredDocumentCardProps> = ({
  document,
  selectedRole: externalSelectedRole,
  onClick,
  viewMode = 'list' // Default to list view (hide role-specific content)
}) => {
  const priority = priorityConfig[document.priority] || priorityConfig.low;

  // Internal state for role switching and expandable sections
  const [internalSelectedRole, setInternalSelectedRole] = useState<string>(
    externalSelectedRole || document.role_tailored_content?.[0]?.role || ''
  );
  const [isDetailedChangesExpanded, setIsDetailedChangesExpanded] = useState<boolean>(false);

  // Sync internal role with external role when it changes
  useEffect(() => {
    if (externalSelectedRole !== undefined) {
      setInternalSelectedRole(externalSelectedRole);
    }
  }, [externalSelectedRole]);

  // Use external role if provided, otherwise use internal state
  const activeRole = externalSelectedRole || internalSelectedRole;

  // Get role-specific content
  const roleContent = document.role_tailored_content?.find(
    content => content.role === activeRole
  ) || document.role_tailored_content?.[0];

  // Get available roles for tabs
  const availableRoles = document.role_tailored_content || [];

  return (
    <Card
      className={`hover:shadow-lg transition-all cursor-pointer border-l-4 ${priority.borderColor}`}
      style={{ borderLeftColor: priority.color }}
      onClick={onClick}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-lg font-semibold text-gray-900 flex-1">
            {document.title}
          </CardTitle>
          <Badge
            className={`${priority.bgColor} ${priority.textColor} border ${priority.borderColor} font-semibold`}
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            {priority.label}
          </Badge>
        </div>

        {document.date && (
          <p className="text-sm text-gray-500 mt-1">
            Published: {new Date(document.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Role Tabs - Only show in detail view if multiple roles available */}
        {viewMode === 'detail' && availableRoles.length > 1 && (
          <div className="border-b border-gray-200 pb-3">
            <div className="flex gap-2 flex-wrap">
              {availableRoles.map((roleData) => {
                const isActive = roleData.role === activeRole;
                const displayName = roleData.role_display_name ||
                  roleData.role.split('_').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');

                return (
                  <button
                    key={roleData.role}
                    onClick={(e) => {
                      e.stopPropagation();
                      setInternalSelectedRole(roleData.role);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* One-Sentence Summary */}
        {document.simplified_analysis?.one_sentence_summary && (
          <div className="bg-slate-50 border-l-4 border-slate-400 p-3">
            <p className="text-sm font-medium text-slate-900">
              {document.simplified_analysis.one_sentence_summary}
            </p>
          </div>
        )}

        {/* Affected Departments */}
        {document.affected_departments && document.affected_departments.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-600 mb-2 flex items-center">
              <Building2 className="w-3 h-3 mr-1" />
              AFFECTED DEPARTMENTS
            </h4>
            <div className="flex flex-wrap gap-2">
              {document.affected_departments.map((dept, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs"
                >
                  {dept}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Role-Specific Summary - Only show in detail view */}
        {viewMode === 'detail' && roleContent?.summary && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              {roleContent.role_display_name
                ? `Summary for ${roleContent.role_display_name}`
                : roleContent.role
                ? `Summary for ${roleContent.role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`
                : 'Summary'}
            </h4>
            <p className="text-sm text-gray-700 leading-relaxed">
              {roleContent.summary}
            </p>
          </div>
        )}

        {/* Financial Implications - Only show in detail view */}
        {viewMode === 'detail' && roleContent?.financial_implications && (
          <div className="bg-green-50 border border-green-100 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-green-900 mb-1 flex items-center">
              <DollarSign className="w-4 h-4 mr-1" />
              Financial Implications
            </h4>
            <p className="text-sm text-gray-700">
              {roleContent.financial_implications}
            </p>
          </div>
        )}

        {/* Time Impact / Deadline - Only show in detail view */}
        {viewMode === 'detail' && (roleContent?.time_impact || roleContent?.deadline) && (
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-purple-900 mb-1 flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              Timeline & Urgency
            </h4>
            <div className="space-y-1">
              {roleContent.time_impact && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Impact: </span>
                  {roleContent.time_impact}
                </p>
              )}
              {roleContent.deadline && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Deadline: </span>
                  <span className="text-purple-700 font-semibold">
                    {roleContent.deadline}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Compliance Checklist - Show role-specific in detail view, general in list view */}
        {viewMode === 'detail' && roleContent?.key_actions && roleContent.key_actions.length > 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-amber-900 mb-3 flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {roleContent.role_display_name
                ? `${roleContent.role_display_name} Action Items`
                : 'Role-Specific Action Items'}
            </h4>
            <ul className="space-y-2">
              {roleContent.key_actions.map((action, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="mt-0.5 flex-shrink-0 w-4 h-4 border-2 border-amber-600 rounded bg-white" />
                  <span className="text-sm text-gray-700 flex-1">{action}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : viewMode === 'list' && document.simplified_analysis?.key_requirements && document.simplified_analysis.key_requirements.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-amber-900 mb-3 flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Compliance Checklist
            </h4>
            <ul className="space-y-2">
              {document.simplified_analysis.key_requirements.slice(0, 3).map((requirement, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="mt-0.5 flex-shrink-0 w-4 h-4 border-2 border-amber-600 rounded bg-white" />
                  <span className="text-sm text-gray-700 flex-1">{requirement}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Detailed Changes - Expandable */}
        {document.simplified_analysis?.detailed_changes && document.simplified_analysis.detailed_changes.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsDetailedChangesExpanded(!isDetailedChangesExpanded);
              }}
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                <FileText className="w-4 h-4 mr-2" />
                Detailed Changes ({document.simplified_analysis.detailed_changes.length})
              </h4>
              {isDetailedChangesExpanded ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
            {isDetailedChangesExpanded && (
              <div className="p-4 bg-white">
                <ul className="space-y-2">
                  {document.simplified_analysis.detailed_changes.map((change, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-600 mt-1.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Metadata Bar */}
        <div className="pt-3 border-t border-gray-200 bg-gray-50 -mx-6 px-6 -mb-6 pb-6 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {document.date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Effective Date</p>
                  <p className="font-medium text-gray-900">
                    {new Date(document.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            )}
            {document.source && (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Source Agency</p>
                  <p className="font-medium text-gray-900">{document.source}</p>
                </div>
              </div>
            )}
            {(document.source_link || document.url) && (
              <div className="flex items-center gap-2">
                <a
                  href={document.source_link || document.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-4 h-4" />
                  View Original Document
                </a>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RoleTailoredDocumentCard;
